import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { FirstLeadGuidance } from 'app/features/leads/components/first-lead-guidance';
import { LeadsGuideModal } from 'app/features/leads/components/guide/leads-guide-modal';
import { LeadsEmptyState } from 'app/features/leads/components/leads-empty-state';
import { LeadsFilteredEmpty } from 'app/features/leads/components/leads-filtered-empty';
import { LeadsTable } from 'app/features/leads/components/leads-table';
import { LeadsToolbar } from 'app/features/leads/components/leads-toolbar';
import Button from 'app/ui/components/atoms/button';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import { leadsListQueryOptions } from 'app/features/leads/queries/leads-list-query-options';
import type { LeadStatusFilter, LeadSummary } from 'app/features/leads/services/gym-finder/types';
import { getLeadsPageState } from 'app/features/leads/utils/get-leads-page-state';
import { routes } from 'app/routes/constants';
import ErrorComponent from 'app/ui/components/molecules/error';
import GenericPage from 'app/ui/components/layouts/generic-page';
import Loading from 'app/ui/components/molecules/loading';
import PageTitle from 'app/ui/components/molecules/page-title';
import Typography from 'app/ui/components/atoms/typography';
import { trackEvent } from 'app/utils/analytics/amplitude/tracker';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  margin: ${({ theme: { spacing } }) => `${spacing(8)} 0 ${spacing(6)}`};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};
`;

const LeadsPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const pageTitle = intl.formatMessage({ id: 'leads.pageTitle' });

  const [status, setStatus] = useState<LeadStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // Search and status flow through the data-layer query options so React Query's
  // cache key drives refetching — no ad hoc local filtering on top of the result.
  const { data, isPending, isError } = useQuery(leadsListQueryOptions({ status, search }));

  useEffect(() => {
    trackEvent('leads_page_viewed', {});
  }, []);

  const handleOpenGuide = useCallback(() => {
    trackEvent('leads_guide_opened', {});
    setIsGuideOpen(true);
  }, []);

  const handleCloseGuide = useCallback(() => {
    setIsGuideOpen(false);
  }, []);

  const handleViewDetails = useCallback(
    (lead: LeadSummary) => {
      trackEvent('leads_view_details_clicked', { leadId: lead.id });
      navigate(routes.LEAD_DETAIL.replace(':leadId', lead.id));
    },
    [navigate],
  );

  if (isPending) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorComponent />;
  }

  const pageState = getLeadsPageState({
    totalCount: data.totalCount,
    filteredCount: data.leads.length,
    profileComplete: data.profileComplete,
    search,
    status,
  });

  const isProfileIncomplete = pageState === 'zero-leads-profile-incomplete';

  const handleFirstLeadView = () => {
    const [lead] = data.leads;
    if (lead) {
      handleViewDetails(lead);
    }
  };

  const leadCountLabel = intl.formatMessage({ id: 'leads.count' }, { count: data.leads.length });
  const guideTriggerLabel = intl.formatMessage({ id: 'leads.guide.trigger' });

  return (
    <GenericPage pageTitle={pageTitle}>
      <HeaderRow>
        <PageTitle>
          <Typography variant="h3">{pageTitle}</Typography>
        </PageTitle>
        <HeaderActions>
          <Typography variant="body2">{leadCountLabel}</Typography>
          <span data-testid="leads-guide-trigger">
            <Button type="secondary-white" label={guideTriggerLabel} onClick={handleOpenGuide} />
          </span>
        </HeaderActions>
      </HeaderRow>

      {isProfileIncomplete ? <LeadsEmptyState /> : null}

      {pageState === 'first-lead' ? (
        <>
          <FirstLeadGuidance onViewLead={handleFirstLeadView} />
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsTable leads={data.leads} onViewDetails={handleViewDetails} />
        </>
      ) : null}

      {pageState === 'filtered-empty' ? (
        <>
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsFilteredEmpty />
        </>
      ) : null}

      {!isProfileIncomplete && pageState === 'table' ? (
        <>
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsTable leads={data.leads} onViewDetails={handleViewDetails} />
        </>
      ) : null}

      <LeadsGuideModal isOpen={isGuideOpen} onClose={handleCloseGuide} />
    </GenericPage>
  );
};

const LeadsPage = () => (
  <LeadsQueryProvider>
    <LeadsPageContent />
  </LeadsQueryProvider>
);

export default LeadsPage;
